import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button } from 'react-bootstrap'
import CustomModal from '../../../CustomModal/CustomModal'
import { assignSiteToSpaceAsync } from '../../../../Manage/actions'
import PrivateSpaceIcon from '../PrivateSpaceIcon/PrivateSpaceIcon'
import styles from './AssignToSpaceModal.module.css'

const AssignToSpaceModal = ({ isOpen, onClose, site }) => {
  const dispatch = useDispatch()
  const { spaces = [] } = useSelector((state) => state.manage)
  const [selectedSpaceId, setSelectedSpaceId] = useState(site?.spaceId || null)
  const [isAssigning, setIsAssigning] = useState(false)
  const [isRemoving, setIsRemoving] = useState(false)
  const [error, setError] = useState('')

  const handleSpaceSelect = (spaceId) => {
    setSelectedSpaceId(spaceId)
    setError('')
  }

  const handleSubmit = async () => {
    if (!site?.subdomain) {
      setError('Invalid site information')
      return
    }

    if (!selectedSpaceId) {
      setError('Please select a space before assigning.')
      return
    }
    setIsAssigning(true)
    setError('')

    try {
      await dispatch(assignSiteToSpaceAsync(site.subdomain, selectedSpaceId))
      onClose()
    } catch (error) {
      setError(error.message || 'Failed to assign project to space. Please try again.')
    } finally {
      setIsAssigning(false)
    }
  }

  const handleRemoveFromSpace = async () => {
    if (!site?.subdomain) {
      setError('Invalid site information')
      return
    }
    setIsRemoving(true)
    setError('')

    try {
      await dispatch(assignSiteToSpaceAsync(site.subdomain, null))
      onClose()
    } catch (error) {
      setError(error.message || 'Failed to remove project from space. Please try again.')
    } finally {
      setIsRemoving(false)
    }
  }

  return (
    <CustomModal show={isOpen} onHide={onClose} centered size="md" enforceFocus={false} dialogClassName={styles.modal}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h3 className={styles.title}>Assign to a space</h3>
          <p className={styles.subtitle}>Select a space to move this project to</p>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.spacesList}>
          {spaces?.length > 0 ? (
            spaces.map((space) => (
              <div
                key={space.id}
                className={`${styles.spaceItem} ${selectedSpaceId === space.id ? styles.selected : ''}`}
                onClick={() => handleSpaceSelect(space.id)}
              >
                <div className={styles.radio}>
                  <div className={selectedSpaceId === space.id ? styles.radioSelected : ''} />
                </div>
                <div className={styles.spaceInfo}>
                  <div className={styles.spaceName}>
                    {space.isPrivate && <PrivateSpaceIcon />}
                    {space.name}
                  </div>
                  <div className={styles.spaceDescription}>Move project to {space.name}</div>
                </div>
              </div>
            ))
          ) : (
            <div className={styles.emptyState}>No spaces available</div>
          )}
        </div>

        <div className={styles.footer}>
          <Button variant="outline-secondary" onClick={onClose} disabled={isAssigning || isRemoving}>
            Cancel
          </Button>
          {site?.spaceId && selectedSpaceId === site?.spaceId && (
            <Button
              variant="outline-danger"
              onClick={handleRemoveFromSpace}
              disabled={isRemoving || isAssigning}
              className={styles.removeButton}
            >
              {isRemoving ? 'Removing...' : 'Remove'}
            </Button>
          )}
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isAssigning || isRemoving || !selectedSpaceId || selectedSpaceId === site?.spaceId}
            className={styles.submitButton}
          >
            {isAssigning ? 'Assigning...' : 'Assign'}
          </Button>
        </div>
      </div>
    </CustomModal>
  )
}

export default AssignToSpaceModal
